import React, { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AnimatedPanel, PopoverOverlay, PopoverPanel } from './styles';
import { breakpoints } from '../../constants/responsive';

export type PopoverProps = {
  open: boolean;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLElement>;
  children: React.ReactNode;
  width?: number;
  mobileBreakpoint?: number;
  align?: 'left' | 'right';
  className?: string;
};

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

const Popover: React.FC<PopoverProps> = ({
  open,
  onClose,
  anchorRef,
  children,
  width,
  mobileBreakpoint = breakpoints.mobile,
  align = 'left',
  className
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const [isMobile, setIsMobile] = React.useState(
    typeof window !== 'undefined' ? window.innerWidth <= mobileBreakpoint : false
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= mobileBreakpoint);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [mobileBreakpoint]);

  useEffect(() => {
    if (!open) return;

    previousFocusRef.current = document.activeElement as HTMLElement;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== 'Tab' || !panelRef.current) return;

      const focusable = Array.from(
        panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)
      ).filter(el => !el.hasAttribute('disabled') && el.offsetParent !== null);

      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const targetEl = event.target as HTMLElement | null;

      // Menus renderizados em portal (SearchableSelect, Semantic UI, etc.)
      if (
        targetEl?.closest?.('[data-filter-portal-menu="true"]') ||
        targetEl?.closest?.('.ui.dropdown .menu') ||
        targetEl?.closest?.('.visible.menu.transition')
      ) {
        return;
      }

      if (
        panelRef.current &&
        !panelRef.current.contains(target) &&
        anchorRef.current &&
        !anchorRef.current.contains(target)
      ) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);

    const timer = window.setTimeout(() => {
      const first = panelRef.current?.querySelector<HTMLElement>(FOCUSABLE);
      first?.focus();
    }, 0);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
      window.clearTimeout(timer);
      previousFocusRef.current?.focus();
    };
  }, [open, onClose, anchorRef]);

  const getPanelStyle = useCallback((): React.CSSProperties => {
    if (isMobile || !anchorRef.current) return {};

    const rect = anchorRef.current.getBoundingClientRect();
    const panelWidth = width || 320;
    let left = rect.left + window.scrollX;

    if (align === 'right') {
      left = rect.right + window.scrollX - panelWidth;
    }

    const viewportWidth = window.innerWidth;
    if (left + panelWidth > viewportWidth - 8) {
      left = Math.max(8, viewportWidth - panelWidth - 8);
    }
    if (left < 8) left = 8;

    return {
      position: 'absolute',
      top: rect.bottom + window.scrollY + 6,
      left,
      width: panelWidth,
      zIndex: 1000
    };
  }, [align, anchorRef, isMobile, width]);

  if (!open) return null;

  const content = (
    <>
      {isMobile && <PopoverOverlay onClick={onClose} />}
      <PopoverPanel
        ref={panelRef}
        mobile={isMobile}
        width={width}
        className={className}
        style={isMobile ? undefined : getPanelStyle()}
        role="dialog"
        aria-modal="true"
      >
        <AnimatedPanel>{children}</AnimatedPanel>
      </PopoverPanel>
    </>
  );

  return createPortal(content, document.body);
};

export default Popover;
